import { SiteUrls } from '@tableau/shared-urls';

import { UrlParamKeys } from '../../app/UrlParamKeys';
import { WebClientListResourceFetcher } from '../../commonInfra/utils/resourceFetchers/WebClientListResourceFetcher';
import { IResult } from '../../httpClient/serverApi/types/IResponse';
import { RequestHelper } from '../../httpClient/utils/RequestHelper';
import { ResultHelper } from '../../httpClient/utils/ResultHelper';
import { WebHttpClientWithErrorToastInstance } from '../../httpClient/WebHttpClientWithErrorToast';
import { IListFetcher } from '../../ts/fetchers/IListFetcher';
import { Filter } from '../../ts/helpers/Filter';
import { ServerApiMapper } from '../../ts/helpers/ServerModelHelper';
import { IDataAlertInfo } from '../../ts/models/IDataAlertInfo';
import { Set } from '../../ts/models/Set';
import { AddUsersToDataAlertsParams, AddUsersToDataAlertsRequest } from '../../ts/ServerApi/requests/AddUsersToDataAlertsRequest';
import { DeleteDataAlertsRequest } from '../../ts/ServerApi/requests/DeleteDataAlertsRequest';
import { GetDataAlertsRequest } from '../../ts/ServerApi/requests/GetDataAlertsRequest';
import { RemoveUsersFromDataAlertsParams, RemoveUsersFromDataAlertsRequest } from '../../ts/ServerApi/requests/RemoveUsersFromDataAlertsRequest';
import { ResumeDataAlertsRequest } from '../../ts/ServerApi/requests/ResumeDataAlertsRequest';
import { SetDataAlertsOwnerRequest } from '../../ts/ServerApi/requests/SetDataAlertsOwnerRequest';
import { ILeafFilterClause } from '../../ts/ServerApi/types/IFilterClause';
import { NamedItemKeys } from '../../ts/ServerApi/types/resourceParts';
import { IListResource } from './../../ts/resources/Resource';
import { IResourceQuery, ResourceQuery } from './../../ts/resources/ResourceQuery';


export class DataAlertsResource implements IListResource<IDataAlertInfo> {
  private listFetcher: IListFetcher<IDataAlertInfo>;

  constructor() {
    this.listFetcher = new WebClientListResourceFetcher(GetDataAlertsRequest, ServerApiMapper.dataAlerts);
  }

  private getErrorNotifyingServer() {
    return WebHttpClientWithErrorToastInstance;
  }

  public all(): IResourceQuery<IDataAlertInfo> {
    return new ResourceQuery(this.listFetcher);
  }

  public getById(id: string, additionalFilterClauses?: ILeafFilterClause[]): Promise<IDataAlertInfo> {
    let query = this.all();
    if(additionalFilterClauses) query = query.withFilters(additionalFilterClauses);
    return query.addFilter(Filter.stringFieldEq(NamedItemKeys.id, id))
    .getFirst();
  }

  public resumeAlerts(ids: string[]): Promise<IResult> {
    return this.getErrorNotifyingServer().sendRequests(
      RequestHelper.buildRequests(ids, idsBlock => new ResumeDataAlertsRequest(idsBlock)),
      ResultHelper.combineResults);
  }

  public del(ids: string[]): Promise<IResult> {
    return this.getErrorNotifyingServer().sendRequests(
      RequestHelper.buildRequests(ids, idsBlock => new DeleteDataAlertsRequest(idsBlock)),
      ResultHelper.combineResults);
  }

  public setOwner(ids: string[], ownerId: string): Promise<IResult> {
    return this.getErrorNotifyingServer().sendRequestsAndDelay(
      RequestHelper.buildRequests(ids, (idsBlock) => new SetDataAlertsOwnerRequest(idsBlock, ownerId)),
      ResultHelper.combineResults);
  }

  public removeUsers(params: RemoveUsersFromDataAlertsParams): Promise<IResult> {
    return this.getErrorNotifyingServer().sendRequests(
      RequestHelper.buildRequests(params.dataAlertIds,
          idsBlock => new RemoveUsersFromDataAlertsRequest({ dataAlertIds: idsBlock, userIds: params.userIds })),
      ResultHelper.combineResults);
  }

  public addUsers(params: AddUsersToDataAlertsParams): Promise<IResult> {
    return this.getErrorNotifyingServer().sendRequests(
      RequestHelper.buildRequests(params.dataAlertIds,
          idsBlock => new AddUsersToDataAlertsRequest({ dataAlertIds: idsBlock, userIds: params.userIds })),
      ResultHelper.combineResults);
  }

  public userIsSubscribedToAllAlerts(alerts: Set<IDataAlertInfo>): boolean {
    return alerts.all(a => a.isCurrentUserSubscribed && a.recipientsCount > 0);
  }

  public userIsSubscribedToAnyAlerts(alerts: Set<IDataAlertInfo>): boolean {
    return alerts.any(a => a.isCurrentUserSubscribed && a.recipientsCount > 0);
  }

  public alertOnVizPath(alert: IDataAlertInfo, siteUrls: SiteUrls): string {
      let editPath = siteUrls.viewUrl(alert.context.path) + '?' + UrlParamKeys.EditDataAlert + '=' + alert.id;
      if (alert.context.type !== 'customizedView') {
        editPath += '&' + UrlParamKeys.OriginalView +'=y';
      }
      if (alert.suspendState !== 'NotSuspended') {
        editPath += '&' + UrlParamKeys.ResumeDataAlert + '=true';
      }
      return editPath;
  }

}

export const DataAlerts = new DataAlertsResource;
